import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TabBar from '@/components/feature/TabBar';
import ProfileHeader from '@/components/feature/ProfileHeader';
import OrderStatus from '@/components/feature/OrderStatus';
import AssetCards from '@/components/feature/AssetCards';
import ProfileMenu from '@/components/feature/ProfileMenu';
import { getUserInfo, logout } from '@/api';
import { removeToken } from '@/api/client';
import type { ProfileData } from '@/api';
import type { ProfileUser, OrderStatus as OrderStatusItem, AssetItem, MenuItem } from '@/mocks/profile';

//订单状态图标映射
const orderIcons: Record<string, string> = {
  待签名: 'ri-quill-pen-line',
  待买方签名: 'ri-time-line',
  待确认: 'ri-bank-card-line',
  可挂卖: 'ri-secure-payment-line',
  已完成: 'ri-check-double-line',
  挂卖中: 'ri-archive-2-line',
};

//资产卡片图标映射
const assetIcons: Record<string, string> = {
  代金券储备: 'ri-coupon-3-line',
  优惠券: 'ri-ticket-line',
  我的奖金: 'ri-coin-line',
  推广佣金: 'ri-money-cny-circle-line',
};

//资产卡片跳转路径映射
const assetPaths: Record<string, string> = {
  代金券储备: '/voucher-details',
  优惠券: '/coupon-details',
  我的奖金: '/bonus-details',
  推广佣金: '/commission-details',
};

const menuItems: MenuItem[] = [
  { id: 'm1', label: '实名认证', icon: 'ri-shield-check-line', iconColor: 'text-red-500', path: '/verify' },
  { id: 'm2', label: '签约中心', icon: 'ri-layout-masonry-line', iconColor: 'text-purple-500', path: '/agreements' },
  { id: 'm3', label: '我的地址', icon: 'ri-map-pin-line', iconColor: 'text-red-500', path: '/address' },
  { id: 'm4', label: '分销中心', icon: 'ri-share-line', iconColor: 'text-blue-500', path: '/team' },
  { id: 'm5', label: '二维码', icon: 'ri-qr-code-line', iconColor: 'text-green-500', path: '/invite' },
];

export default function Profile() {
  const navigate = useNavigate();
  const [data, setData] = useState<ProfileData | null>(null);
  const [error, setError] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  useEffect(() => {
    let mounted = true;
    getUserInfo()
      .then((res) => {
        if (mounted) setData(res);
      })
      .catch((err) => {
        if (mounted) setError(err instanceof Error ? err.message : '加载失败');
      });
    return () => {
      mounted = false;
    };
  }, []);

  //用户信息映射
  const user: ProfileUser | null = data
    ? {
        name: data.user.nickname || '用户',
        phone: data.user.phone_masked || '',
        avatar: data.user.avatar || '',
        inviteCode: data.user.invite_code,
        isVerified: data.user.realname_status === 2,
      }
    : null;

  //买卖订单状态映射
  const mapOrders = (orders: { label: string; count: number }[], prefix: string): OrderStatusItem[] =>
    orders.map((o, i) => ({
      id: `${prefix}${i}`,
      label: o.label,
      icon: orderIcons[o.label] || 'ri-file-list-3-line',
      count: o.count,
    }));

  //资产卡片映射
  const mapAssets = (assets: ProfileData['assets']): AssetItem[] =>
    assets.map((a, i) => ({
      id: `a${i}`,
      amount: Number(a.amount ?? 0).toFixed(2),
      label: a.label,
      subLabel: a.subLabel || undefined,
      variant: i % 2 === 0 ? 'gold' : 'blue',
      icon: assetIcons[a.label] || 'ri-wallet-3-line',
      path: assetPaths[a.label],
    }));

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const res = await getUserInfo();
      setData(res);
      setError('');
      showToast('刷新成功');
    } catch (err) {
      showToast(err instanceof Error ? err.message : '刷新失败');
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
    } catch {
      //token 失效也继续本地登出
    } finally {
      removeToken();
      navigate('/login');
    }
  };

  return (
    <div className="mobile-container pb-20">
      {/* Red header area with user info + verified badge */}
      {user ? (
        <ProfileHeader
          user={user}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          onSettings={() => navigate('/edit-profile')}
        />
      ) : (
        <div className="bg-primary-500 px-5 pt-6 pb-8">
          <div className="flex items-center gap-3.5">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
              <i className="ri-user-3-line text-white/70 text-2xl" />
            </div>
            <div>
              <p className="text-white text-base font-bold">
                {error ? '加载失败' : '加载中...'}
              </p>
            </div>
          </div>
        </div>
      )}

      <main className="bg-background-100 px-3 pb-3">
        {data ? (
          <>
            {/* Asset cards */}
            <AssetCards assets={mapAssets(data.assets)} />

            {/* Buyer orders */}
            <OrderStatus title="买方" warehouseLabel="买单仓库" warehousePath="/buyer-warehouse" orders={mapOrders(data.buyerOrders, 'b')} />

            {/* Seller orders（状态与文档3.2.2.3.2对齐：待签名/待确认/已完成） */}
            <OrderStatus
              title="卖方"
              warehouseLabel="卖单仓库"
              warehousePath="/seller-warehouse"
              orders={mapOrders(data.sellerOrders, 's')}
            />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin mb-3" />
            <p className="text-foreground-400 text-sm">{error || '加载中...'}</p>
          </div>
        )}

        {/* Menu */}
        <ProfileMenu items={menuItems} />

        {/* Logout / Login */}
        <button
          onClick={handleLogout}
          disabled={loggingOut}
          className="w-full mt-3 py-3.5 bg-white rounded-2xl text-foreground-500 text-sm font-medium cursor-pointer shadow-sm border border-background-200/50 hover:shadow-md hover:bg-background-50 transition-all active:scale-[0.99] disabled:opacity-60"
        >
          {loggingOut ? '退出中...' : '退出登录'}
        </button>
      </main>

      {/* Toast */}
      {toast && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-sm rounded-2xl text-white text-sm z-50 shadow-xl">
          {toast}
        </div>
      )}

      <TabBar />
    </div>
  );
}
